/**
 * The logo, from the brand kit (`code/UptikLogo.tsx`): a tick inside the minute's
 * ring, with the wordmark beside it in outlined Inter Bold.
 *
 * - `lockup`, the mark and the wordmark, at 20px tall or more.
 * - `mark`, the mark alone, at 20px or more. Below that use `/favicon.svg`,
 *   whose strokes are heavier.
 *
 * **Inline rather than an `<img>` of the kit's files**, because the wordmark is
 * `currentColor` and the colours are `--ud-color-brand-*`, so it follows the
 * theme with no second file and no request on the cold load. The kit's `theme`
 * prop goes with them. The colours themselves are the kit's and only their home
 * moved: hex lives in `build-tokens.mjs`.
 *
 * Do not recolour, rotate or outline it, and the kit's wordmark is lowercase even
 * though the name in a sentence is UpTik.
 */

import { cx } from '@/lib/cx'

export interface UptikLogoProps {
  variant?: 'lockup' | 'mark'
  className?: string
  title?: string
}

const WORDMARK =
  'M12.0234375 0.328125C16.0078125 0.328125 18.6796875 -1.734375 20.15625 -5.4375L20.25 0.0H26.90625V-26.203125H19.8515625V-11.0390625C19.8515625 -7.5703125 17.7421875 -5.5546875 14.765625 -5.5546875C11.8125 -5.5546875 10.03125 -7.5 10.03125 -10.7578125V-26.203125H3.0V-9.5390625C3.0 -3.3984375 6.5859375 0.328125 12.0234375 0.328125Z M30.2428125 9.796875H37.2740625V-4.1953125H37.485C38.469375 -2.0859375 40.625625 0.4453125 44.9146875 0.4453125C50.9615625 0.4453125 55.57875 -4.3359375 55.57875 -13.0546875C55.57875 -22.0078125 50.7740625 -26.53125 44.9615625 -26.53125C40.5084375 -26.53125 38.4459375 -23.8828125 37.485 -21.796875H37.156875V-26.203125H30.2428125ZM42.7584375 -5.203125C39.1725 -5.203125 37.11 -8.34375 37.11 -13.078125C37.11 -17.8359375 39.1490625 -20.90625 42.7584375 -20.90625C46.438125 -20.90625 48.3834375 -17.671875 48.3834375 -13.078125C48.3834375 -8.4609375 46.4146875 -5.203125 42.7584375 -5.203125Z M70.8684375 -26.203125H65.97V-32.4375H58.93875V-26.203125H55.329375V-20.8359375H58.93875V-7.0546875C58.93875 -2.25 61.798125 0.375 67.048125 0.375C68.454375 0.375 70.0246875 0.2109375 71.595 -0.2578125L70.5871875 -5.53125C70.0715625 -5.390625 68.8996875 -5.2265625 68.360625 -5.2265625C66.6496875 -5.2265625 65.97 -6.0 65.97 -7.6640625V-20.8359375H70.8684375Z M72.79875 0.0H79.83V-26.203125H72.79875ZM76.314375 -29.625C78.42375 -29.625 80.1815625 -31.21875 80.1815625 -33.234375C80.1815625 -35.2265625 78.42375 -36.84375 76.314375 -36.84375C74.1815625 -36.84375 72.4471875 -35.2265625 72.4471875 -33.234375C72.4471875 -31.21875 74.1815625 -29.625 76.314375 -29.625Z M83.1665625 0.0H90.1978125V-8.4140625L92.2134375 -10.6875L99.5259375 0.0H107.799375L97.5103125 -14.8359375L107.3071875 -26.203125H99.174375L90.59625 -16.125H90.1978125V-34.921875H83.1665625Z'

function Mark({ strokeWidth }: { strokeWidth: number }) {
  return (
    <g fill="none" strokeWidth={strokeWidth} strokeLinecap="round">
      <path d="M70.16 19.25A42 42 0 1 1 49.84 19.25" stroke="var(--ud-color-brand-ring)" />
      <path d="M40 61L54 75" stroke="var(--ud-color-brand-down)" />
      <path d="M54 75L81 45" stroke="var(--ud-color-brand-up)" />
    </g>
  )
}

export function UptikLogo({ variant = 'lockup', className, title = 'UpTik' }: UptikLogoProps) {
  const size = 'h-[calc(24*var(--ud-u))] w-auto shrink-0'
  if (variant === 'mark') {
    return (
      <svg viewBox="0 0 120 120" className={cx(size, className)} role="img" aria-label={title}>
        <Mark strokeWidth={11} />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 162.51 52" className={cx(size, className)} role="img" aria-label={title}>
      <g transform="scale(0.43333)">
        <Mark strokeWidth={12} />
      </g>
      <path transform="translate(59 38.556)" fill="currentColor" d={WORDMARK} />
    </svg>
  )
}
